// 고객 알림(알림톡·문자) — 사건·채널·발송 로그 모양. 실제 발송은 channels.ts, 고르기는 plan.ts.

import { OrgScopedRow } from "../common/types";

export const CUSTOMER_NOTIFY_EVENTS = [
  "contract.signed",
  "project.log_added",
  "change_order.requested",
  "project.completed",
  "project.message_posted",
  "intake.received",
] as const;
export type CustomerNotifyEvent = (typeof CUSTOMER_NOTIFY_EVENTS)[number];
// 현장이 있어야 보내는 사건 — 상담 접수 확인만 문의 단계(현장 없음)다.
export type ProjectCustomerNotifyEvent = Exclude<CustomerNotifyEvent, "intake.received">;

export const CUSTOMER_NOTIFY_EVENT_LABEL: Record<CustomerNotifyEvent, string> = {
  "contract.signed": "계약 체결",
  "project.log_added": "현장일지 작성",
  "change_order.requested": "변경공사 승인 요청",
  "project.completed": "준공",
  "project.message_posted": "담당자 대화",
  "intake.received": "상담 접수 확인",
};

export const CUSTOMER_NOTIFY_CHANNELS = ["log", "alimtalk", "sms"] as const;
export type CustomerNotifyChannelName = (typeof CUSTOMER_NOTIFY_CHANNELS)[number];

export const CUSTOMER_NOTIFY_CHANNEL_LABEL: Record<CustomerNotifyChannelName, string> = {
  log: "기록",
  alimtalk: "알림톡",
  sms: "문자",
};

export const CUSTOMER_NOTIFY_STATUSES = ["sent", "failed", "skipped"] as const;
export type CustomerNotifyStatus = (typeof CUSTOMER_NOTIFY_STATUSES)[number];

// 한 달 건수 — sent 는 실제 발송(과금 기준) · logOnly 는 번호 있는 skipped(plan 의 skip 은 타입상 recipient_masked: null 이라 기록 채널 행뿐). failed 는 세지 않는다.
export interface CustomerNotifyMonthCount {
  sent: number;
  logOnly: number;
}

// 고치지 않는 장부라 updated_at 이 없다(`0034`).
export interface CustomerNotifyLog extends Omit<OrgScopedRow, "updated_at"> {
  // 현장이 지워져도 월 발송 건수는 남아야 해서 null 이 될 수 있다(`on delete set null`).
  project_id: string | null;
  event: CustomerNotifyEvent;
  channel: CustomerNotifyChannelName;
  recipient_masked: string | null;
  status: CustomerNotifyStatus;
  error: string | null;
  dedupe_key: string;
  payload: Record<string, unknown>;
}

export const isCustomerNotifyEvent = (v: unknown): v is CustomerNotifyEvent =>
  typeof v === "string" && (CUSTOMER_NOTIFY_EVENTS as readonly string[]).includes(v);

// 한 사건은 한 번만 — 같은 원천(계약·일지·변경공사·현장)이면 같은 키라 두 번째는 표의 unique 가 막는다.
export const customerNotifyDedupeKey = (event: CustomerNotifyEvent, sourceId: string): string =>
  `${event}:${sourceId}`;

// 현장일지 알림의 원천 — 현장·일지 날짜(KST) 하나에 한 통.
export const projectLogNotifySource = (projectId: string, logDate: string): string => `${projectId}:${logDate}`;

// 조직 설정 `customer_notify.off` (끈 사건의 JSON 배열) — 깨진 값·모르는 코드는 버려서 기본은 전부 켬.
export const parseCustomerNotifyOff = (value: string | null): CustomerNotifyEvent[] => {
  if (!value) return [];
  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) ? Array.from(new Set(parsed.filter(isCustomerNotifyEvent))) : [];
  } catch {
    return [];
  }
};
